import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

/**
 * Settings -> User Accounts, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Users_accounts.php`:
 *
 *   index                     -> GET  /settings/users_accounts            (bara)
 *   active/blocked + usersData-> GET  /settings/users_accounts/accounts
 *   addUserModal              -> GET  /settings/users_accounts/accounts/new
 *   add_user                  -> POST /settings/users_accounts/accounts
 *   getActiveTabs             -> GET  .../accounts/{id}/tabs
 *   userDetails               -> GET  .../accounts/{id}
 *   editUser / update_user    -> GET/POST .../accounts/{id}/edit, .../accounts/{id}
 *   remove/block/activate     -> POST .../accounts/{id}/{remove|block|activate}
 *   changePhoto / update_photo/ remove_photo
 *                             -> GET/POST .../accounts/{id}/photo (+ /remove)
 *   edit_password             -> POST .../accounts/{id}/password
 *   privacySettings           -> GET/POST .../accounts/{id}/privacy
 *   userEvents                -> GET  .../accounts/{id}/events
 *   userPermissions           -> GET/POST .../accounts/{id}/permissions
 *   userVideoConference       -> GET/POST .../accounts/{id}/video
 *   events                    -> GET  .../events
 *   groups / groupsData       -> GET  .../groups
 *   add_group / update_group  -> POST .../groups, .../groups/{id}
 *   enable/disable_group      -> POST .../groups/{id}/state
 *   signature + list + forms  -> GET  .../signatures
 *   create/update/remove sablon
 *                             -> POST .../signatures[/{id}][/remove]
 *   work_hour_plans           -> GET  .../plans
 *   create/update/remove plan -> POST .../plans[/{id}][/remove]
 *   userRoles / roles_data    -> GET  .../roles
 *   add/update/delete role    -> POST .../roles[/{id}][/remove]
 *   getRoleTabs               -> GET  .../roles/{id}
 *   saveRoleDefaults          -> POST .../roles/{id}/permissions
 *   removeRoleModule          -> POST .../roles/{id}/modules/{module}/remove
 *   resources_management      -> GET  .../resources
 *   create/save/delete resursa-> POST .../resources[/{id}], .../resources/delete
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * FILELE VIN FILTRATE PE DREPTURI DE PE SERVER, si bara, si cele opt dinauntrul
 * unui cont. Ecranul nu stie ce drepturi are omul si nu ascunde nimic singur -
 * o poarta desenata in browser nu e o poarta.
 */

/** O fila din bara de sus. `parent` = "Accounts", lista care se desface. */
export type UsersTab = {
  id: string
  label: string
  icon: string
  group: string
  parent: string
  right: string
}

export type UsersShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server. */
  tabs: UsersTab[]
  companyId: number
  /** Ce poate face omul: butoanele Remove/Block, si scrierile in general. */
  may: Record<string, boolean>
}

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export const TABS = [
  'active',
  'blocked',
  /* Nu e in bara - n-a fost nici in portal - dar calea exista si acolo. */
  'inactive',
  'groups',
  'events',
  'signature',
  'work_hour_plans',
  'resources_management',
  'userRoles',
] as const

export type UsersTabId = (typeof TABS)[number]

export function isUsersTab(value: string): value is UsersTabId {
  return (TABS as readonly string[]).includes(value)
}

export const usersShellQuery = queryOptions({
  queryKey: ['settings', 'users', 'shell'],
  queryFn: () => apiGet<UsersShell>('/settings/users_accounts'),
})

/* -------------------------------------------------------------------------
 * Cererea unei liste (aceeasi forma pe toate sase)
 * ---------------------------------------------------------------------- */

export type UsersTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function usersTableParams(r: UsersTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

/* -------------------------------------------------------------------------
 * Conturi
 * ---------------------------------------------------------------------- */

/** Un rand din lista de conturi - `ActiveUsersModel` din `settings/users.js`. */
export type UserRow = {
  ID: number
  firstname: string
  lastname: string
  username: string
  email: string
  status: number
  group_name: string | null
  role_name: string | null
  it_manager: number
  last_login: string | null
  last_seen: string | null
  last_mobile_login: string | null
  phone: string | null
  position: string | null
  plan_name: string | null
  created: string | null
  internal_rank: string | null
  has_quick_chat_support: number
  /** De unde vine contul (`AccountOrigin` pe server). */
  origin: 'local' | 'directory' | 'scim' | 'sso'
  origin_label: string
  origin_hint: string
}

export function usersAccountsQuery(status: string, r: UsersTableRequest) {
  const params = usersTableParams(r)
  params.set('status', status)
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', status, r],
    queryFn: () => apiGet<TablePage<UserRow>>(`/settings/users_accounts/accounts?${params.toString()}`),
  })
}

export type Choice = { id: number; name: string }

export type Country = { id: number; name: string; prefix: string }

/**
 * `hidden`: treapta nu se mai ofera (vechiul Super Admin 4, GPS Dispatcher 5);
 * vine numai pentru contul care o are deja. `help`: textul "i"-ului.
 */
export type AccessLevel = { value: number; label: string; help?: string; hidden?: boolean }

export type NewAccountForm = {
  departments: Choice[]
  countries: Country[]
  signatures: Choice[]
  roles: Choice[]
  accessLevels: AccessLevel[]
  /** Regula de parola, spusa INAINTE - `PasswordPolicy::hint()`. */
  passwordHint: string
}

export const newAccountFormQuery = queryOptions({
  queryKey: ['settings', 'users', 'accounts', 'new'],
  queryFn: () => apiGet<NewAccountForm>('/settings/users_accounts/accounts/new'),
})

/**
 * Contul nou. Pleaca `multipart/form-data`, ca in portal: formularul poarta si
 * o poza.
 */
export function createAccount(form: FormData) {
  return apiPostForm<{ saved: boolean; id: number; mailed: boolean }>('/settings/users_accounts/accounts', form)
}

export type AccountTab = { id: string; label: string; right: string }

export function accountTabsQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'tabs'],
    queryFn: () => apiGet<{ tabs: AccountTab[] }>(`/settings/users_accounts/accounts/${id}/tabs`),
  })
}

export type AccountDetails = {
  id: number
  firstname: string
  lastname: string
  username: string
  email: string
  phone: string
  mobilePhone: string
  position: string
  address: string
  internalRank: string
  status: number
  itManager: boolean
  collaborator: boolean
  quickChatSupport: boolean
  companyId: number
  departmentId: number
  departmentName: string
  countryId: number
  countryName: string
  roleId: number
  accessId: number
  gpsTracking: number
  onSite: number
  car: number
  created: string
  picture: string
  /** Poza de pe serverul de descarcari; '' cand n-are sau nu e configurat. */
  avatarUrl: string
  /**
   * De unde vine `accessId` (numai in formularul de editare): `role` = din
   * rolul principal; `manual` sau null = pus de mana, ca azi.
   */
  accessSource?: AccessSource | null
  /**
   * Managerul trimis de IAM prin provizionare (SCIM / REST), numai in fila
   * Details; null = niciunul. Numai de citit - nu se schimba din portal.
   */
  iamManager?: IamManager | null
}

/** `ScimManager::toRest` pe server. `linked` = exista contul managerului la noi. */
export type IamManager = {
  id: number | null
  username: string | null
  external_id: string | null
  name: string | null
  email: string | null
  linked: boolean
  /** null cand nu e legat. */
  active: boolean | null
}

export type AccessSource = 'role' | 'manual'

export function accountQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'details'],
    queryFn: () => apiGet<{ user: AccountDetails }>(`/settings/users_accounts/accounts/${id}`),
  })
}

export type AccountEditForm = {
  user: AccountDetails
  companies: Choice[]
  departments: Choice[]
  countries: Country[]
  /** `defaultAccess`: nivelul dat de rol (`it_roles.default_access`); null = nesetat. */
  roles: Array<Choice & { defaultAccess?: number | null }>
  accessLevels: AccessLevel[]
}

export function accountEditQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'edit'],
    queryFn: () => apiGet<AccountEditForm>(`/settings/users_accounts/accounts/${id}/edit`),
  })
}

export function saveAccount(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}`, fields)
}

export function removeAccount(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/remove`, {})
}

export function blockAccount(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/block`, {})
}

export function activateAccount(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/activate`, {})
}

export function accountPhotoQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'photo'],
    queryFn: () => apiGet<{ file: string }>(`/settings/users_accounts/accounts/${id}/photo`),
  })
}

export function uploadPhoto(id: number, form: FormData) {
  return apiPostForm<{ saved: boolean; file: string }>(`/settings/users_accounts/accounts/${id}/photo`, form)
}

export function removePhoto(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/photo/remove`, {})
}

export function changePassword(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; mailed: boolean }>(`/settings/users_accounts/accounts/${id}/password`, fields)
}

export type PrivacySettings = {
  timezones: Choice[]
  signatures: Choice[]
  plans: Choice[]
  settings: {
    timezone: number
    signatureTemplate: number
    workHourPlan: number
    imei: string
    imei2: string
    enableNotifications: boolean
    autoAssign: boolean
  }
}

export function privacyQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'privacy'],
    queryFn: () => apiGet<PrivacySettings>(`/settings/users_accounts/accounts/${id}/privacy`),
  })
}

export function savePrivacy(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/privacy`, fields)
}

export type VideoSettings = { videoConference: boolean; withAudio: boolean }

export function videoQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'video'],
    queryFn: () => apiGet<{ settings: VideoSettings }>(`/settings/users_accounts/accounts/${id}/video`),
  })
}

export function saveVideo(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/video`, fields)
}

/* -------------------------------------------------------------------------
 * Drepturi (ale unui om si ale unui rol) - acelasi desen
 * ---------------------------------------------------------------------- */

export type PermissionModule = { id: number; name: string; slug: string; icon: string }

export type PermissionResource = {
  id: number
  moduleId: number
  name: string
  slug: string
  permission: number
  /**
   * Numele celor patru trepte. Peste tot Read/Create/Edit/Delete; Adam Intop
   * are alte doua scari (`permissions.php`, `$odLevelNames`).
   */
  levels: string[]
}

export type PermissionsPage = {
  userId: number
  modules: PermissionModule[]
  resources: PermissionResource[]
  /** Nevid = drepturile vin numai din grupurile IAM (randurile de aici se sterg la intrare). */
  iamLocked?: string
}

export function permissionsQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'permissions'],
    queryFn: () => apiGet<PermissionsPage>(`/settings/users_accounts/accounts/${id}/permissions`),
  })
}

/**
 * "Fara drept" nu e o valoare care se trimite, ci LIPSA ei: capatul sterge tot
 * si insereaza doar ce primeste (`Role_modules::updateUserModules`).
 */
export function savePermissions(id: number, access: Record<string, number>) {
  const fields: Record<string, PostValue> = {}
  for (const [key, value] of Object.entries(access)) fields[`access[${key}]`] = value
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/permissions`, fields)
}

/* -------------------------------------------------------------------------
 * Rolurile contului: principal + suplimentare (numai v4, cerinta CEC L18)
 *
 * Rolul principal ramane `users.role_id` (il vad si portalul CEC si ph4.api)
 * si se schimba din formularul "Edit user". Rolurile suplimentare le stie
 * numai v4; drepturile efective sunt uniunea tuturor. Serverul decide ce se
 * poate da (`grantable`) - nimeni nu da un rol cu drepturi pe care nu le are.
 * ---------------------------------------------------------------------- */

export type RoleRef = { id: number; name: string; fromDirectory?: boolean }

/** Contul e gestionat din grupurile IAM ale unui furnizor (SSO / AD). */
export type AccountIam = {
  providerId: number
  provider: string
  companies: boolean
  extraRoles: boolean
  keepManual: boolean
  /** Firmele de azi, cand vin din grupuri. */
  companyNames: string[]
}

export type GrantableRole = RoleRef & { grantable: boolean }

export type RoleAuditEntry = {
  at: string
  actor: string
  /** `add` / `remove` / `primary` */
  action: string
  role: string
  previous: string
}

export type AccountRoles = {
  userId: number
  primary: RoleRef | null
  extra: RoleRef[]
  available: GrantableRole[]
  mayAssign: boolean
  /** null / lipsa = contul se gestioneaza de mana, ca azi. */
  iam?: AccountIam | null
  audit: RoleAuditEntry[]
}

export function accountRolesQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'roles'],
    queryFn: () => apiGet<AccountRoles>(`/settings/users_accounts/accounts/${id}/roles`),
  })
}

export function addAccountRole(id: number, roleId: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/roles`, { role_id: roleId })
}

export function removeAccountRole(id: number, roleId: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/roles/${roleId}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Vizibilitatea tichetelor (cerinta CEC, 16 octombrie 2026): treapta rolului
 * si grupurile de tichete ale omului. Numai v4.
 * ---------------------------------------------------------------------- */

/** `all` = ce vede si azi; `groups` = grupurile lui + ale lui; `own` = numai ale lui. */
export type TicketScope = 'all' | 'groups' | 'own'

export type TicketScopeOption = { value: TicketScope; label: string }

export type RoleTicketScope = {
  roleId: number
  scope: TicketScope
  options: TicketScopeOption[]
  mayEdit: boolean
}

export type TicketGroup = {
  id: number
  name: string
  /** manual / iam / scim */
  source: string
  /** Venit din IAM / SCIM: se scoate din director, nu de aici. */
  fromDirectory: boolean
}

export type AccountTicketGroups = {
  userId: number
  /** `users_info.DepartamentID` - departamentul contului, numarat si el ca grup. */
  primary: { id: number; name: string } | null
  groups: TicketGroup[]
  available: Array<{ id: number; name: string }>
  effective: {
    scope: TicketScope
    label: string
    admin: boolean
    roles: Array<{ id: number; name: string; scope: TicketScope; label: string }>
  }
  mayEdit: boolean
}

export function roleTicketScopeQuery(roleId: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'roles', roleId, 'ticket-scope'],
    queryFn: () => apiGet<RoleTicketScope>(`/settings/users_accounts/roles/${roleId}/ticket-scope`),
  })
}

export function saveRoleTicketScope(roleId: number, scope: TicketScope) {
  return apiPost<{ saved: boolean; scope: TicketScope }>(`/settings/users_accounts/roles/${roleId}/ticket-scope`, {
    scope,
  })
}

/* -------------------------------------------------------------------------
 * Nivelul de acces pe ROL (19 octombrie 2026): "Access in the platform" in
 * Roles and Resources. null = "Not set", ca azi. Numai v4.
 * ---------------------------------------------------------------------- */

export type RoleAccessOption = { value: number | null; label: string; help: string }

export type RoleAccess = {
  roleId: number
  level: number | null
  options: RoleAccessOption[]
  mayEdit: boolean
  /** Service Provider (3) se ofera numai administratorului de platforma. */
  mayGiveProvider: boolean
  /** Cati oameni ai rolului isi iau accesul din el. */
  members: number
}

export function roleAccessQuery(roleId: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'roles', roleId, 'access'],
    queryFn: () => apiGet<RoleAccess>(`/settings/users_accounts/roles/${roleId}/access`),
  })
}

export function saveRoleAccess(roleId: number, level: number | null) {
  return apiPost<{ saved: boolean; level: number | null; updated: number }>(`/settings/users_accounts/roles/${roleId}/access`, {
    level: level === null ? '' : level,
  })
}

export function accountTicketGroupsQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'ticket-groups'],
    queryFn: () => apiGet<AccountTicketGroups>(`/settings/users_accounts/accounts/${id}/ticket-groups`),
  })
}

export function addAccountTicketGroup(id: number, departmentId: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/ticket-groups`, {
    department_id: departmentId,
  })
}

export function removeAccountTicketGroup(id: number, departmentId: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/accounts/${id}/ticket-groups/${departmentId}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Evenimente
 * ---------------------------------------------------------------------- */

export type UserEventRow = {
  id: number
  event_date: string
  event_message: string
  authorName: string | null
  authorFirstname: string | null
}

export function usersEventsQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'users', 'events', r],
    queryFn: () => apiGet<TablePage<UserEventRow>>(`/settings/users_accounts/events?${usersTableParams(r).toString()}`),
  })
}

export function accountEventsQuery(id: number, r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'users', 'accounts', id, 'events', r],
    queryFn: () =>
      apiGet<TablePage<UserEventRow>>(
        `/settings/users_accounts/accounts/${id}/events?${usersTableParams(r).toString()}`,
      ),
  })
}

/* -------------------------------------------------------------------------
 * Grupuri de conturi
 * ---------------------------------------------------------------------- */

export type GroupRow = {
  ID: number
  group_name: string
  group_email: string | null
  dispatching_email: string | null
  phone: string | null
  status: number
  created: string | null
}

export function usersGroupsQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'users', 'groups', r],
    queryFn: () => apiGet<TablePage<GroupRow>>(`/settings/users_accounts/groups?${usersTableParams(r).toString()}`),
  })
}

export const groupFormQuery = queryOptions({
  queryKey: ['settings', 'users', 'groups', 'new'],
  queryFn: () => apiGet<{ ticketTypes: Choice[] }>('/settings/users_accounts/groups/new'),
})

export type GroupDetails = {
  id: number
  name: string
  email: string
  dispatchingEmail: string
  phone: string
  level: number
  active: boolean
  quickChatSupport: boolean
  assignChatTicket: boolean
  quickEscalation: boolean
  startHour: string
  endHour: string
}

export function groupQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'groups', id],
    queryFn: () =>
      apiGet<{ group: GroupDetails; ticketTypes: Choice[]; selectedTypes: number[] }>(
        `/settings/users_accounts/groups/${id}`,
      ),
  })
}

export function createGroup(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/users_accounts/groups', fields)
}

export function saveGroup(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/groups/${id}`, fields)
}

export function setGroupState(id: number, enabled: boolean) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/groups/${id}/state`, { enabled: enabled ? 1 : 0 })
}

/* -------------------------------------------------------------------------
 * Semnaturi
 * ---------------------------------------------------------------------- */

export type SignatureTemplate = {
  id: number
  name: string
  body: string
  updated: string
  companyId: number
}

export type SignatureVariable = { variable: string; name: string }

export const signaturesQuery = queryOptions({
  queryKey: ['settings', 'users', 'signatures'],
  queryFn: () =>
    apiGet<{ templates: SignatureTemplate[]; variables: SignatureVariable[]; companies: Choice[] }>(
      '/settings/users_accounts/signatures',
    ),
})

export function createSignature(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/users_accounts/signatures', fields)
}

export function saveSignature(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/signatures/${id}`, fields)
}

export function removeSignature(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/signatures/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Planuri de ore de lucru
 * ---------------------------------------------------------------------- */

export type PlanDay = {
  enabled: boolean
  startHour: string
  endHour: string
  startHourExtended: string
  endHourExtended: string
  nightPlan: boolean
}

export const plansQuery = queryOptions({
  queryKey: ['settings', 'users', 'plans'],
  queryFn: () => apiGet<{ plans: Choice[] }>('/settings/users_accounts/plans'),
})

export function planQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'plans', id],
    queryFn: () => apiGet<{ plan: Choice; days: Record<string, PlanDay> }>(`/settings/users_accounts/plans/${id}`),
  })
}

export function createPlan(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/users_accounts/plans', fields)
}

export function savePlan(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/plans/${id}`, fields)
}

export function removePlan(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/plans/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Roluri
 * ---------------------------------------------------------------------- */

export type RoleRow = {
  id: number
  name: string
  created_at: string
  /** `null` = rol de sistem: nu se redenumeste si nu se sterge de aici. */
  sp: number | null
}

export function rolesQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'users', 'roles', r],
    queryFn: () => apiGet<TablePage<RoleRow>>(`/settings/users_accounts/roles?${usersTableParams(r).toString()}`),
  })
}

export type RoleDefaults = {
  role: { id: number; name: string; slug: string; sp: number | null }
  modules: PermissionModule[]
  resources: PermissionResource[]
}

export function roleQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'roles', id, 'defaults'],
    queryFn: () => apiGet<RoleDefaults>(`/settings/users_accounts/roles/${id}`),
  })
}

export function createRole(name: string) {
  return apiPost<{ saved: boolean; id: number }>('/settings/users_accounts/roles', { role_name: name })
}

export function renameRole(id: number, name: string) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/roles/${id}`, { name })
}

export function deleteRole(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/roles/${id}/remove`, {})
}

export function saveRoleDefaults(id: number, access: Record<string, number>, removeModules: number[]) {
  const fields: Record<string, PostValue> = {}
  for (const [key, value] of Object.entries(access)) fields[`access[${key}]`] = value
  removeModules.forEach((moduleId, i) => {
    fields[`remove[${i}]`] = String(moduleId)
  })
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/roles/${id}/permissions`, fields)
}

export function removeRoleModule(id: number, moduleId: number) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/roles/${id}/modules/${moduleId}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Resurse
 * ---------------------------------------------------------------------- */

export type ResourceRow = {
  id: number
  module_id: number
  name: string
  slug: string
  module_name: string | null
}

export function resourcesQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'users', 'resources', r],
    queryFn: () =>
      apiGet<TablePage<ResourceRow>>(`/settings/users_accounts/resources?${usersTableParams(r).toString()}`),
  })
}

export type ResourceModule = { id: number; name: string; slug: string }

export const resourceFormQuery = queryOptions({
  queryKey: ['settings', 'users', 'resources', 'new'],
  queryFn: () => apiGet<{ modules: ResourceModule[] }>('/settings/users_accounts/resources/new'),
})

export function resourceQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'users', 'resources', id],
    queryFn: () =>
      apiGet<{ resource: { id: number; moduleId: number; name: string; slug: string }; modules: ResourceModule[] }>(
        `/settings/users_accounts/resources/${id}`,
      ),
  })
}

export function createResource(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/users_accounts/resources', fields)
}

export function saveResource(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/users_accounts/resources/${id}`, fields)
}

export function deleteResource(id: number, slug: string) {
  return apiPost<{ saved: boolean }>('/settings/users_accounts/resources/delete', { id, slug })
}
